import React, {useState} from "react";
import {useTranslation} from "react-i18next";

// formik
import {useFormik} from 'formik';
import * as yup from 'yup';

// react-router-dom components
import {Link} from "react-router-dom";

// Auth
import RedirectAuth from "../../../auth/RedirectAuth";

import {useSignInMutation} from "../query";
import MDLoadingButton from "../../../components/MDLoadingButton";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// Material Dashboard 2 React components
import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";
import MDInput from "materialDashboard/components/MDInput";
import Stack from "@mui/material/Stack";
import PasswordInput from "../../../components/inputs/PasswordInput";


function SignIn() {
    const {t} = useTranslation(['auth', 'validation']);
    const [errorMessage, setErrorMessage] = useState('');
    const {mutate, isLoading, isError} = useSignInMutation({onError: (error) => setErrorMessage(error?.response?.data?.detail)});

    const handleSetRememberMe = () => {
        formik.setFieldValue("rememberMe", !formik.values.rememberMe);
    }

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            rememberMe: true,
        }, validationSchema: yup.object({
            email: yup
                .string()
                .email(t('validation:email'))
                .required(t('validation:required')),
            password: yup
                .string()
                .required(t('validation:required')),
            rememberMe: yup
                .boolean()
        }),
        onSubmit: async (values) => {
            mutate(values);
        },
    });

    return <RedirectAuth>
        <Card>
            <MDBox
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                mx={2}
                mt={-3}
                p={2}
                mb={1}
                textAlign="center"
            >
                <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                    {t('signIn.title')}
                </MDTypography>
            </MDBox>

            {isError && <MDBox pt={4}>
                <MDTypography color="error" variant="body2" textAlign="center">
                    {errorMessage ?? t('signIn.error')}
                </MDTypography>
            </MDBox>
            }

            <MDBox pt={4} pb={3} px={3}>
                <MDBox component="form" role="form" onSubmit={formik.handleSubmit}>
                    <MDBox mb={2}>
                        <MDInput name="email"
                                 type="email"
                                 label="Email"
                                 value={formik.values.email}
                                 onChange={formik.handleChange}
                                 error={isError || (formik.touched.email && Boolean(formik.errors.email))}
                                 helperText={formik.touched.email && formik.errors.email}
                                 fullWidth/>
                    </MDBox>
                    <MDBox mb={2}>
                        <PasswordInput name="password"
                                       label="Password"
                                       value={formik.values.password}
                                       onChange={formik.handleChange}
                                       error={isError || (formik.touched.password && Boolean(formik.errors.password))}
                                       helperText={formik.touched.password && formik.errors.password}
                                       fullWidth/>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" ml={-1}>
                        <Switch checked={formik.values.rememberMe} onChange={handleSetRememberMe}/>
                        <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            onClick={handleSetRememberMe}
                            sx={{cursor: "pointer", userSelect: "none", ml: -1}}
                        >
                            &nbsp;&nbsp;{t('signIn.rememberMe')}
                        </MDTypography>
                    </MDBox>

                    <MDBox mt={4} mb={1}>
                        <MDLoadingButton variant="gradient" color="info" fullWidth type="submit" loading={isLoading}>
                            {t('signIn.submit')}
                        </MDLoadingButton>
                    </MDBox>

                    <Stack mt={3} mb={1} justifyContent="center" alignItems="center">
                        <MDTypography
                            component={Link}
                            to="/auth/forgot-password"
                            variant="button"
                            color="info"
                            fontWeight="medium"
                            textGradient
                        >
                            {t('signIn.forgotPassword')}
                        </MDTypography>
                        <MDTypography
                            component={Link}
                            to="/auth/email-confirmation"
                            variant="button"
                            color="text"
                            fontWeight="medium"
                            textGradient
                        >
                            {t('signIn.emailConfirmation')}
                        </MDTypography>
                    </Stack>
                </MDBox>
            </MDBox>
        </Card>
    </RedirectAuth>;
}

export default SignIn;
